/**
 * Normalizes URL paths for use in Sentry tags and fingerprints
 * to avoid high-cardinality issues and prevent leaking sensitive identifiers.
 *
 * Replaces:
 * - Numeric IDs (e.g., /websites/123/ -> /websites/<id>/)
 * - 26-character alphanumeric hashes (e.g., invite tokens -> /<hash>)
 * - UUIDs (e.g., /abc-def-123 -> /<uuid>)
 * - Other long alphanumeric segments that might be tokens
 *
 * Handles both full URLs and paths, stripping query parameters and fragments.
 *
 * @param url - The URL or path to normalize
 * @returns Normalized path suitable for Sentry tags/fingerprints
 *
 * @example
 * normalizeSentryUrl('/websites/123/patients/456')
 * // Returns: '/websites/<id>/patients/<id>'
 *
 * normalizeSentryUrl('/invite/ABC123XYZ987654321ABCDEF')
 * // Returns: '/invite/<hash>'
 *
 * normalizeSentryUrl('https://example.com/users/123?foo=bar#section')
 * // Returns: '/users/<id>'
 */
export function normalizeSentryUrl(url: string): string {
  if (!url) return 'unknown'

  let path: string

  // Try to parse as full URL to extract pathname
  try {
    // If url is a path, this will use window.location.origin as base
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
    path = parsed.pathname
  } catch {
    // Fallback: treat as path and manually strip query and hash
    path = url.split('?')[0].split('#')[0]
  }

  return (
    path
      // Replace 26-character alphanumeric hashes (invite tokens, etc.)
      // Add boundary to prevent partial replacement of longer tokens
      .replace(/\/[A-Z0-9]{26}(?=\/|$)/gi, '/<hash>')
      // Replace UUIDs (8-4-4-4-12 format)
      .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi, '/<uuid>')
      // Replace numeric IDs (1 to 10 digits)
      .replace(/\/\d{1,10}(?=\/|$)/g, '/<id>')
      // Replace long alphanumeric segments (potential tokens, 20+ chars)
      .replace(/\/[A-Za-z0-9_-]{20,}(?=\/|$)/g, '/<token>')
  )
}
